import React from 'react';
import { PageTile, TableRow, TableRowType } from '@make-software/cspr-design';
import { AccountInfoResult, NFTTokenResult } from '@block-explorer/entities';
import { Trans, useTranslation } from 'react-i18next';
import {
  PaginationProps,
  TableViewType,
} from '../table-settings/table-settings';
import NftTile from './nft-tile';
import { useCreateAccountsInfoGetter } from '../../hooks';
import { SortingProps, TableDataHeader } from '@block-explorer/ui';
import NFTTokensListItem from '../nft-token-list-item/nft-token-list-item';
import { PageDataTable } from '../page-data-table/page-data-table';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import type { NftTableViewId } from '../../redux/table-view';
import TilesGrid from '../tiles-grid/tiles-grid';

export interface NftsGridProps {
  tableId: NftTableViewId;
  data?: NFTTokenResult[] | null;
  loading: boolean;
  error?: string | null;
  paginationProps: PaginationProps;
  sortingProps: SortingProps;
  filter?: JSX.Element;
  actions?: JSX.Element | JSX.Element[];
  showOwnerInfo?: boolean;
  showContractPackageInfo?: boolean;
}

interface NftsTilesProps extends NftsGridProps {
  getAccountInfo: <T>(publicKey: string) => T | null | undefined;
  hasNothingFound: boolean;
  loadingAccountsInfo: boolean;
}

const NftsTable = ({
  tableId,
  data,
  loading,
  error,
  paginationProps,
  sortingProps,
  filter,
  actions,
  showOwnerInfo,
  showContractPackageInfo = true,
  getAccountInfo,
  hasNothingFound,
  loadingAccountsInfo,
}: NftsTilesProps) => {
  const { t } = useTranslation();

  return (
    <PageTile>
      <PageDataTable<NFTTokenResult>
        renderTopActions={() => actions || []}
        loading={loading}
        data={data ?? null}
        error={error}
        sortingProps={sortingProps}
        tableId={tableId}
        tableRowType={TableRowType.TextWithAvatar}
        paginationProps={paginationProps}
        noDataError={hasNothingFound}
        renderDataHeader={({ sortingProps }) => (
          <TableRow>
            {showContractPackageInfo ? (
              <TableDataHeader fixedWidthRem={15.625} filter={filter}>
                <Trans t={t}>Contract</Trans>
              </TableDataHeader>
            ) : null}
            <TableDataHeader fixedWidthRem={15.625}>
              <Trans t={t}>Token ID</Trans>
            </TableDataHeader>
            <TableDataHeader fixedWidthRem={9.375}>
              <Trans t={t}>Name</Trans>
            </TableDataHeader>
            <TableDataHeader fixedWidthRem={15.625}>
              <Trans t={t}>Description</Trans>
            </TableDataHeader>
            <TableDataHeader
              fixedWidthRem={9.375}
              {...sortingProps}
              sortableColumnField="timestamp"
              reverseSortingDirection
            >
              <Trans t={t}>Age</Trans>
            </TableDataHeader>
            {showOwnerInfo ? (
              <TableDataHeader fixedWidthRem={13.75} filter={filter}>
                <Trans t={t}>Owner</Trans>
              </TableDataHeader>
            ) : null}
            <TableDataHeader>Standard</TableDataHeader>
          </TableRow>
        )}
        renderPaginatedData={(tokens: NFTTokenResult[]) =>
          tokens.map((token, idx) => (
            <NFTTokensListItem
              key={`${token.contract_package_hash}-${token.token_id}-${idx}`}
              data={token}
              loading={loading || loadingAccountsInfo}
              accountInfo={
                token.owner_public_key
                  ? getAccountInfo<AccountInfoResult>(token.owner_public_key) ||
                    undefined
                  : undefined
              }
              showOwnerInfo={showOwnerInfo}
              showContractPackageInfo={showContractPackageInfo}
            />
          ))
        }
      />
    </PageTile>
  );
};

const NftsTiles = ({
  tableId,
  data,
  loading,
  error,
  paginationProps,
  filter,
  actions,
  showOwnerInfo,
  showContractPackageInfo = true,
  getAccountInfo,
  hasNothingFound,
  loadingAccountsInfo,
}: NftsTilesProps) => {
  const { t } = useTranslation();
  return (
    <TilesGrid
      tableId={tableId}
      data={data}
      error={error}
      loading={loading || loadingAccountsInfo}
      hasNothingFound={hasNothingFound}
      paginationProps={paginationProps}
      filter={filter}
      actions={actions}
      totalRowsLabel={t('tokens')}
      renderPaginatedData={() =>
        data &&
        data.map((token) => (
          <NftTile
            key={`${token.contract_package_hash}-${token.token_id}`}
            token={token}
            showOwnerInfo={showOwnerInfo}
            showContractPackageInfo={showContractPackageInfo}
            getAccountInfo={getAccountInfo}
          />
        ))
      }
    />
  );
};

export const NftsGrid = (props: NftsGridProps) => {
  const { tableId, data, loading } = props;
  const viewType =
    useSelector((state: RootState) => state.tablesView[tableId]) ??
    TableViewType.secondary;

  const publicKeysList =
    data == null ? null : data.map((d) => d.owner_public_key);

  const accountHashesList = data == null ? null : data.map((d) => d.owner_hash);

  const { getAccountInfo, loading: loadingAccountsInfo } =
    useCreateAccountsInfoGetter(publicKeysList, accountHashesList);

  const hasNothingFound = !data?.length && !loading && !loadingAccountsInfo;

  if (viewType === TableViewType.table) {
    return (
      <NftsTable
        {...props}
        getAccountInfo={getAccountInfo}
        hasNothingFound={hasNothingFound}
        loadingAccountsInfo={loadingAccountsInfo}
      />
    );
  }

  return (
    <NftsTiles
      {...props}
      getAccountInfo={getAccountInfo}
      hasNothingFound={hasNothingFound}
      loadingAccountsInfo={loadingAccountsInfo}
    />
  );
};

export default NftsGrid;
